import axiosInstance, { extractErrorMessage } from './axiosConfig';
import type { RegisterRequest, EmailVerificationRequest, RefreshTokenRequest,
              TokenResponse, AuthApiResponse, UserResponse} from '../types/auth';

// Define the base URL for the API
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api/v1`;

/**
 * Login user with email and password
 * 
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise with token response data
 * 
 * @example
 * ```typescript
 * try {
 *   const tokens = await login('user@example.com', 'password123');
 *   console.log('Login successful:', tokens);
 * } catch (error) {
 *   console.error('Login failed:', error);
 * }
 * ```
 */
export const login = async (email: string, password: string): Promise<TokenResponse> => {
  try {
    const response = await axiosInstance.post<AuthApiResponse<TokenResponse>>(
      `${API_BASE_URL}/auth/login`,
      new URLSearchParams({
        username: email, // Backend expects 'username' field for OAuth2PasswordRequestForm
        password,
      }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    const tokenData = response.data.data;
    if (!tokenData) {
      throw new Error('Token data not received');
    }

    // Store both tokens
    localStorage.setItem('token', tokenData.access_token);
    localStorage.setItem('refreshToken', tokenData.refresh_token);

    // Dispatch event to notify AuthContext to refresh user data
    window.dispatchEvent(new CustomEvent('authStateChanged'));

    return tokenData;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Erro no login'
      )
    );
  }
};

/**
 * Register new user
 * 
 * @param userData - User registration data
 * @returns Promise with user response data
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await register({
 *     email: 'user@example.com',
 *     name: 'John Doe',
 *     password: 'password123',
 *     phone: '(48) 99999-9999'
 *   });
 *   console.log('Registration successful:', response);
 * } catch (error) {
 *   console.error('Registration failed:', error);
 * }
 * ```
 */
export const register = async (userData: RegisterRequest): Promise<AuthApiResponse<UserResponse>> => {
  try {
    const response = await axiosInstance.post<AuthApiResponse<UserResponse>>(
      `${API_BASE_URL}/auth/register`,
      userData
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)), 
        "Erro no cadastro")
      );
  }
};

/**
 * Verify email with verification code
 * 
 * @param email - User's email address
 * @param code - 6-digit verification code
 * @returns Promise with verification response
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await verifyEmail('user@example.com', '123456');
 *   console.log('Email verified:', response);
 * } catch (error) {
 *   console.error('Verification failed:', error);
 * }
 * ```
 */
export const verifyEmail = async (email: string, code: string): Promise<AuthApiResponse> => {
  try {
    const verificationData: EmailVerificationRequest = { email, code };
    const response = await axiosInstance.post<AuthApiResponse>(
      `${API_BASE_URL}/auth/verify-email`,
      verificationData
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(err instanceof Error ? err : new Error(String(err)), 
      "Erro na verificação de e-mail")
    );
  }
};

/**
 * Resend verification email
 * 
 * @param email - User's email address
 * @returns Promise with resend response
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await resendVerificationEmail('user@example.com');
 *   console.log('Verification email resent:', response);
 * } catch (error) {
 *   console.error('Failed to resend email:', error);
 * }
 * ```
 */
export const resendVerificationEmail = async (email: string): Promise<AuthApiResponse> => {
  try {
    const response = await axiosInstance.post<AuthApiResponse>(
      `${API_BASE_URL}/auth/resend-verification`,
      null,
      {
        params: { email }
      }
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(err instanceof Error ? err : new Error(String(err)), 
      "Erro ao reenviar email de verificação")
    );
  }
};

/**
 * Request password reset
 * 
 * @param email - User's email address
 * @returns Promise with reset request response
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await requestPasswordReset('user@example.com');
 *   console.log('Password reset requested:', response);
 * } catch (error) {
 *   console.error('Failed to request reset:', error);
 * }
 * ```
 */
export const requestPasswordReset = async (email: string): Promise<AuthApiResponse> => {
  try {
    const response = await axiosInstance.post<AuthApiResponse>(
      `${API_BASE_URL}/auth/request-reset`,
      null,
      {
        params: { email }
      }
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(err instanceof Error ? err : new Error(String(err)), 
      "Erro ao solicitar redefinição de senha")
    );
  }
};

/**
 * Reset password (requires authentication)
 * 
 * @param newPassword - New password
 * @returns Promise with reset response
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await resetPassword('newpassword123');
 *   console.log('Password reset successful:', response);
 * } catch (error) {
 *   console.error('Failed to reset password:', error);
 * }
 * ```
 */
export const resetPassword = async (newPassword: string): Promise<AuthApiResponse> => {
  try {
    const response = await axiosInstance.post<AuthApiResponse>(
      `${API_BASE_URL}/auth/reset-password`,
      null,
      {
        params: { new_password: newPassword }
      }
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Erro ao redefinir senha'
      )
    );
  }
};

/**
 * Refresh access token using refresh token
 * 
 * @param refreshTokenValue - Current refresh token
 * @returns Promise with new token response
 * 
 * @example
 * ```typescript
 * try {
 *   const tokens = await refreshToken('current_refresh_token');
 *   console.log('Tokens refreshed:', tokens);
 * } catch (error) {
 *   console.error('Failed to refresh tokens:', error);
 * }
 * ```
 */
export const refreshToken = async (refreshTokenValue: string): Promise<TokenResponse> => {
  try {
    const refreshData: RefreshTokenRequest = { refresh_token: refreshTokenValue };
    const response = await axiosInstance.post<AuthApiResponse<TokenResponse>>(
      `${API_BASE_URL}/auth/refresh`,
      refreshData
    );

    const tokenData = response.data.data;
    if (!tokenData) {
      throw new Error('Token data not received');
    }

    // Store new tokens
    localStorage.setItem('token', tokenData.access_token);
    localStorage.setItem('refreshToken', tokenData.refresh_token);

    return tokenData;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Erro ao atualizar token'
      )
    );
  }
};

/**
 * Logout user (clear local storage)
 * 
 * @example
 * ```typescript
 * logout();
 * console.log('User logged out');
 * ```
 */
export const logout = (): void => {
  // Remove authentication tokens
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');

  // CRITICAL: Remove workspace ID to prevent cross-user contamination
  // Without this, next user inherits previous user's workspace causing:
  // - Token consumption in wrong workspace
  // - Incorrect permissions
  // - Data leakage between users
  localStorage.removeItem('activeWorkspaceId');

  // Clear sessionStorage completely (may contain temporary sensitive data)
  sessionStorage.clear();

  // Clear any cached queries or temporary data (but preserve user preferences)
  // This prevents data leakage between users in shared environments
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.startsWith('cache_') ||
      key.startsWith('workspace_') ||
      key.startsWith('query_') ||
      key.startsWith('temp_')
    )) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));

  // Dispatch event to notify AuthContext that user logged out
  window.dispatchEvent(new CustomEvent('authStateChanged'));
};

/**
 * Check if user is authenticated
 * 
 * @returns True if user has valid token, false otherwise
 * 
 * @example
 * ```typescript
 * if (isAuthenticated()) {
 *   console.log('User is logged in');
 * } else {
 *   console.log('User needs to log in');
 * }
 * ```
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('token');
  return !!token && token !== 'undefined' && token !== 'null';
};

/**
 * Get current access token
 * 
 * @returns Current access token or null if not authenticated
 * 
 * @example
 * ```typescript
 * const token = getAccessToken();
 * if (token) {
 *   console.log('Current token:', token);
 * }
 * ```
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Get current refresh token
 * 
 * @returns Current refresh token or null if not available
 * 
 * @example
 * ```typescript
 * const refreshToken = getRefreshToken();
 * if (refreshToken) {
 *   console.log('Current refresh token:', refreshToken);
 * }
 * ```
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem('refreshToken');
}; 